import { api } from './apiSlice'
import { createSlice } from '@reduxjs/toolkit'
import appInsights from '../../applicationInsights'

const AVAILABILITY_REFRESH_ATTEMPTS = 6
const AVAILABILITY_REFRESH_DELAY_MS = 500

const delay = (duration) => new Promise((resolve) => {
    setTimeout(resolve, duration)
})

async function refreshSellerAvailability(dispatch, listingId, expectedStatus) {
    for (let attempt = 0; attempt < AVAILABILITY_REFRESH_ATTEMPTS; attempt += 1) {
        const query = dispatch(
            sellerContentApiSlice.endpoints.getSellerAvailability.initiate(
                listingId,
                { subscribe: false, forceRefetch: true },
            ),
        )
        const availability = await query.unwrap()

        if (availability?.sellerInternalStatus?.toLowerCase() === expectedStatus.toLowerCase()) {
            return
        }

        if (attempt < AVAILABILITY_REFRESH_ATTEMPTS - 1) {
            await delay(AVAILABILITY_REFRESH_DELAY_MS)
        }
    }
}

// Register the Seller tag here rather than on the shared base api.
export const sellerContentApiSlice = api
    .enhanceEndpoints({ addTagTypes: ['Seller', 'SellerAvailability'] })
    .injectEndpoints({
    endpoints: (build) => ({
        getSeller: build.query({
            query: (listingId) => `/partners/sellers/${listingId}`,
            providesTags: (result, error, listingId) => [{ type: 'Seller', id: listingId }],
        }),
        getSellerContent: build.query({
            query: (listingId) => `/partners/sellers/${listingId}/content`,
            providesTags: (result, error, listingId) => [{ type: 'Seller', id: listingId }],
        }),
        getSellerAvailability: build.query({
            query: (listingId) => `/partners/sellers/${listingId}/availability`,
            providesTags: (result, error, listingId) => [{ type: 'SellerAvailability', id: listingId }],
        }),
        updateSellerContent: build.mutation({
            query: (sellerContent) => ({
                url: '/partners/sellers/content',
                method: 'PUT',
                body: sellerContent,
            }),
            invalidatesTags: ['Seller'],
        }),
        updateSellerStatus: build.mutation({
            query: ({ listingId, status }) => ({
                url: `/partners/sellers/${listingId}/status`,
                method: 'PUT',
                body: { status },
            }),
            async onQueryStarted({ listingId, status }, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled
                } catch {
                    // The mutation consumer owns its error notification.
                    return
                }

                try {
                    await refreshSellerAvailability(dispatch, listingId, status)
                } catch (error) {
                    appInsights.ai().trackException({ exception: error })
                }
            },
            invalidatesTags: (result, error, { listingId }) => [
                { type: 'Seller', id: listingId },
            ],
        }),
    }),
})

export const {
    useGetSellerQuery,
    useLazyGetSellerQuery,
    useGetSellerContentQuery,
    useLazyGetSellerContentQuery,
    useGetSellerAvailabilityQuery,
    useUpdateSellerContentMutation,
    useUpdateSellerStatusMutation,
} = sellerContentApiSlice

const sellerContentSlice = createSlice({
    name: 'sellerContent',
    initialState: {},
    reducers: {},
})

export default sellerContentSlice.reducer
